import React, {useContext, useMemo, useRef} from "react";
import {inject, observer} from "mobx-react";
import { RootStore } from 'stores';
import _ from "lodash";
import {
    BaseButton,
    DataTablePresenter, IntegrationHl7VendorType,
    isTabletPortraitDown,
    ITableColumn,
    ProviderIntegrationHl7,
    Spinner,
    ToastContext
} from "@enter/core";
import * as S from "./shared.styles";
import {Icon} from "@mdi/react";
import {mdiArrowLeft, mdiPlus, mdiDelete} from "@mdi/js";
import { DialogContext } from 'services';
import { SecretToggle, ConfirmDialog } from './components';
import {Hl7Modal} from "./hl7-modal/hl7-modal";
import styled from "@emotion/styled/macro";
import dateFns from "date-fns";

const mdyFormat = (d: Date): string => dateFns.format(d, 'MM/DD/YYYY');
const parse = (d: string | undefined): Date => (d ? dateFns.parse(d) : new Date());


export const TableCenteredCell = styled.div`
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    display: flex;
    flex-direction: column;
    text-align: center;
`;

export const Hl7Settings: React.FunctionComponent<{}> = inject('rootStore')(
    observer(
        (props: {}): React.ReactElement => {
            const rootStore: RootStore = _.get(props, 'rootStore');
            if (!rootStore) {
                return <></>;
            }
            const toastContext = useContext(ToastContext);
            const dialogContext = useContext(DialogContext);

            const { settingsStore } = rootStore;

            const columns: ITableColumn<ProviderIntegrationHl7>[] = useMemo(
                () => [
                    {
                        id: 'Vendor Type',
                        Header: () => <S.TableHeader>Vendor Type</S.TableHeader>,
                        Cell: ({ row }): React.ReactElement => <S.TableCell>{row.original.vendorType}</S.TableCell>
                    },
                    {
                        id: 'Security Token',
                        Header: () => <S.TableHeader>Security Token</S.TableHeader>,
                        Cell: ({ row }): React.ReactElement => {
                            return <SecretToggle>{row.original.securityToken}</SecretToggle>
                        }
                    },
                    {
                        id: 'Created Date',
                        maxWidth: 150,
                        Header: () => <S.TableHeader>Created</S.TableHeader>,
                        Cell: ({ row }): React.ReactElement => { return <TableCenteredCell>{mdyFormat(parse(row.original.created))}</TableCenteredCell>}
                    },
                    {
                        id: 'Delete',
                        width: '50px',
                        Cell: ({ row }): React.ReactElement => {

                            const handleDelete = async () => {
                                if(row.original.id) {
                                    console.log("Deleting " + row.original.id);
                                    await settingsStore.deleteHl7(row.original.id);
                                }
                                dialogContext.hideDialog?.();
                            };

                            const handleModalOpen = () => {
                                dialogContext.showDialog?.({
                                    props: dialogProps,
                                    Component: <ConfirmDialog onSubmit={handleDelete} onCancel={handleCancel} />,
                                });
                            }

                            const handleCancel = (): void => {
                                console.log("Cancel delete Integration");
                                dialogContext.hideDialog?.();
                            };

                            const dialogProps = {
                                onClose: handleCancel,
                                disableBackdropClick: true,
                                disableEscapeKeyDown: true,
                                renderTitle: 'Are you sure you want to delete the ' + row.original.vendorType + ' integration?',
                            };

                            return <BaseButton variant="fab-light" size="large" onClick={handleModalOpen}>
                                <Icon path={mdiDelete} size={1} />
                            </BaseButton>;
                        }
                    }
                ],
                [],
            );

            const handleAddKeyClose = (): void => {
                console.log("Closed HL7 Integration Modal Dialog");
                dialogContext.hideDialog();
            };

            const handleOnSave = async (vendorType: IntegrationHl7VendorType): Promise<void> => {
                console.log("Saving integration with " + vendorType);
                await settingsStore.createHl7(vendorType);
                toastContext.notify && toastContext.notify('HL7 Integration Created');
                dialogContext.hideDialog();
            }

            const onClickAddKey = (): void => {
                dialogContext.showDialog({
                    props: {
                        onClose: handleAddKeyClose,
                        disableBackdropClick: true,
                        disableEscapeKeyDown: true,
                        renderTitle: 'Create HL7 Integration',
                    },
                    Component: (
                        <Hl7Modal
                            onClose={handleAddKeyClose}
                            onSubmit={handleOnSave}
                        />
                    ),
                });
            };

            const buildDataPresenter = (): DataTablePresenter<ProviderIntegrationHl7> => {
                return new DataTablePresenter({
                    name: 'integrationHl7s',
                    data: settingsStore.integrationHl7s ? settingsStore.integrationHl7s : [],
                    columns,
                    filterSchemas: [],
                })
            }

            return (!settingsStore.integrationHl7s) ? <Spinner/> : <S.SubCategoryCol>
                <S.SubCategoryItem key="members">
                    <S.SubCategoryItemRow>
                        <S.SubCategoryH1>
                            {isTabletPortraitDown() && (
                                <S.ReturnLink to="/settings">
                                    {(): React.ReactElement => <Icon path={mdiArrowLeft} size={1} />}
                                </S.ReturnLink>
                            )}
                            HL7 Integrations
                        </S.SubCategoryH1>
                        <S.SubCategoryAddButton variant="primary" onClick={onClickAddKey}>
                            <S.SubCategoryPlus path={mdiPlus} size={1} /> Add new HL7 Integration
                        </S.SubCategoryAddButton>
                    </S.SubCategoryItemRow>
                    <S.Table presenter={buildDataPresenter()} hideFiltering={true} hidePagination={true} hideSettings={true} />
                </S.SubCategoryItem>
            </S.SubCategoryCol>;
        },
    ),
);